import pluralize from 'pluralize';
import { type KeyboardEvent, memo, type ReactElement, useCallback, useRef, useState } from 'react';

import { MinusIcon } from '@air/design-icons';
import { IconButton } from '@air/primitive-icon-button';
import { Popover } from '@air/primitive-popover';

import {
  AgentSelectionList,
  type AgentSelectionListRef,
} from '~/components/v2/components/private-selection-bar/agent-selection-bar/components/AgentSelectionList';

export type AgentSelectionItemsDropdownProps = {
  selectedItemCount: number;
  trigger: ReactElement;
};

/**
 * Agent selection bar item-count dropdown — dark glass panel via `Popover`.
 * Callers own trigger chrome; this owns open state, sticky header, and `AgentSelectionList`.
 */
export const AgentSelectionItemsDropdown = memo(({ selectedItemCount, trigger }: AgentSelectionItemsDropdownProps) => {
  const [isOpen, setIsOpen] = useState(false);
  const [scrollElement, setScrollElement] = useState<HTMLElement | null>(null);
  const listRef = useRef<AgentSelectionListRef>(null);

  const handleScrollElementRef = useCallback((node: HTMLDivElement | null) => {
    setScrollElement(node);
  }, []);

  const handleOpenChange = useCallback((open: boolean) => {
    setIsOpen(open);
  }, []);

  const handleClose = useCallback(() => {
    setIsOpen(false);
  }, []);

  const handleScrollKeyDown = useCallback((event: KeyboardEvent<HTMLDivElement>) => {
    listRef.current?.handleListKeyDown(event);
  }, []);

  const handlePanelKeyDown = useCallback(
    (event: KeyboardEvent<HTMLDivElement>) => {
      const { target } = event;

      if (target instanceof Node && scrollElement?.contains(target)) {
        return;
      }

      if (event.key === 'ArrowDown' || event.key === 'ArrowUp' || event.key === 'Home' || event.key === 'End') {
        listRef.current?.handleListKeyDown(event);
      }
    },
    [scrollElement],
  );

  return (
    <Popover
      aria-labelledby="agent-selection-items-dropdown-header"
      className="flex max-h-[min(400px,var(--radix-popover-content-available-height))] w-[min(400px,calc(100vw-2rem))] flex-col overflow-hidden rounded-8 border-2 border-white/15 ads:bg-glass-menu/90 bg-surface-1/90 p-0 text-white shadow-popover outline-none ring-0 backdrop-blur-[24px]"
      data-testid="AGENT_SELECTION_ITEMS_DROPDOWN"
      data-theme="dark"
      modal
      onKeyDown={handlePanelKeyDown}
      onOpenChange={handleOpenChange}
      open={isOpen}
      side="top"
      sideOffset={4}
      trigger={trigger}
    >
      <div className="flex min-h-0 flex-1 flex-col">
        <div className="flex shrink-0 items-center justify-between gap-2.5 border-white/15 border-b px-3 py-2.5">
          <div className="font-medium text-14 text-white" id="agent-selection-items-dropdown-header">
            {`${selectedItemCount} ${pluralize('item', selectedItemCount)} selected`}
          </div>
          <IconButton
            adsIcon
            appearance="ghost"
            className="shrink-0 bg-transparent ads:text-inverse/75 text-white/75 ads:hover:bg-ads-white/10 hover:bg-white/10 ads:hover:text-inverse hover:text-white active:bg-white/15 ads:active:bg-ads-white/15"
            color="gray"
            data-testid="AGENT_SELECTION_ITEMS_DROPDOWN_CLOSE"
            label="Close"
            onClick={handleClose}
            size="small"
            icon={MinusIcon}
          />
        </div>
        <div
          ref={handleScrollElementRef}
          aria-label="Selected items"
          className="min-h-0 flex-1 select-none overflow-y-auto"
          data-testid="AGENT_SELECTION_ITEMS_DROPDOWN_LISTBOX"
          onKeyDown={handleScrollKeyDown}
          role="listbox"
          tabIndex={-1}
        >
          <AgentSelectionList isListOpen={isOpen} ref={listRef} scrollElement={scrollElement} />
        </div>
      </div>
    </Popover>
  );
});

AgentSelectionItemsDropdown.displayName = 'AgentSelectionItemsDropdown';
